(() => {
  const root = document.documentElement;
  const loader = document.querySelector('[data-page-loader]');
  const indicator = loader?.querySelector('[data-page-loader-progress]');
  const bootstrap = window.__remassPageLoader;
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  let finished = false;
  let progress = 0;

  if (!loader || bootstrap?.isComplete?.()) {
    root.classList.remove('is-page-loading');
    root.removeAttribute('aria-busy');
    loader?.remove();
    return;
  }

  const setProgress = (value) => {
    progress = Math.max(progress, Math.min(value, 100));
    indicator?.style.setProperty('--page-load-progress', `${progress}%`);
  };

  const finish = () => {
    if (finished || bootstrap?.isComplete?.()) return;
    finished = true;
    setProgress(100);

    const reveal = bootstrap?.reveal || (() => {
      root.classList.add('is-page-ready');
      root.classList.remove('is-page-loading');
      root.removeAttribute('aria-busy');
      window.setTimeout(() => loader.remove(), 220);
    });

    window.setTimeout(reveal, reducedMotion ? 0 : 120);
  };

  const decodeImage = (image) => {
    if (typeof image.decode !== 'function') return Promise.resolve();
    return image.decode().catch(() => {});
  };

  const waitForImage = (image) => {
    if (image.complete) return decodeImage(image);

    return new Promise((resolve) => {
      const settle = () => {
        decodeImage(image).finally(resolve);
      };

      image.addEventListener('load', settle, { once: true });
      image.addEventListener('error', resolve, { once: true });
    });
  };

  const trackCriticalImages = () => {
    const criticalImages = Array.from(new Set([
      ...document.querySelectorAll('img[data-page-loader-critical]'),
      ...document.querySelectorAll('img[fetchpriority="high"]'),
    ])).filter((image) => !image.currentSrc.startsWith('data:image/gif'));

    if (!criticalImages.length) {
      setProgress(82);
      return Promise.resolve();
    }

    let settled = 0;
    return Promise.allSettled(criticalImages.map((image) => waitForImage(image).finally(() => {
      settled += 1;
      setProgress(30 + settled / criticalImages.length * 52);
    })));
  };

  const waitForEvent = (eventName, ready) => {
    if (ready) return Promise.resolve();
    return new Promise((resolve) => window.addEventListener(eventName, resolve, { once: true }));
  };

  const withTimeout = (promise, timeout) => Promise.race([
    promise,
    new Promise((resolve) => window.setTimeout(resolve, timeout)),
  ]);

  setProgress(6);
  window.requestAnimationFrame(() => setProgress(8));

  const domReady = waitForEvent('DOMContentLoaded', document.readyState !== 'loading').then(() => {
    setProgress(30);
  });

  const imagesReady = domReady.then(() => withTimeout(trackCriticalImages(), 1600));

  domReady.then(() => withTimeout(document.fonts?.ready || Promise.resolve(), 800)).then(() => {
    setProgress(88);
  });

  waitForEvent('load', document.readyState === 'complete').then(() => {
    setProgress(94);
  });

  Promise.all([domReady, imagesReady]).then(() => {
    window.requestAnimationFrame(() => window.requestAnimationFrame(finish));
  });

  const now = window.performance?.now?.() || 0;
  window.setTimeout(finish, Math.max(0, 3000 - now));

  window.addEventListener('pageshow', (event) => {
    if (event.persisted) {
      bootstrap?.reveal?.();
      root.classList.remove('is-page-loading');
      root.removeAttribute('aria-busy');
      loader.remove();
    }
  });
})();
